/**
 * DISCLOSURE — height that unfolds instead of jumping.
 *
 * `Collapse`   mounts/unmounts children with an animated height. For filter
 *              panels, inline validation, expandable rows, "show more".
 * `AutoHeight` keeps children mounted and animates the container whenever
 *              their measured height changes. For a card whose contents swap
 *              (wizard steps, loading → content).
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { collapse } from './presets';
import { DURATION, EASE, tween } from './tokens';

interface CollapseProps {
  open: boolean;
  children: React.ReactNode;
  className?: string;
  /** Animate on first mount when already open. Default off. */
  initial?: boolean;
  id?: string;
  role?: string;
  'aria-live'?: 'polite' | 'assertive' | 'off';
}

export const Collapse: React.FC<CollapseProps> = ({
  open,
  children,
  className,
  initial = false,
  id,
  role,
  'aria-live': ariaLive,
}) => (
  <AnimatePresence initial={initial}>
    {open ? (
      <motion.div
        key="collapse"
        id={id}
        role={role}
        aria-live={ariaLive}
        className={className}
        variants={collapse}
        initial="hidden"
        animate="visible"
        exit="hidden"
        style={{ overflow: 'hidden' }}
      >
        {children}
      </motion.div>
    ) : null}
  </AnimatePresence>
);

interface AutoHeightProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Measures its content with a ResizeObserver and tweens its own height to
 * follow. Overflow is clipped ONLY while the tween runs, so a listbox or a
 * focus ring inside is never cut off at rest.
 */
export const AutoHeight: React.FC<AutoHeightProps> = ({ children, className }) => {
  const inner = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | 'auto'>('auto');
  const [animating, setAnimating] = useState(false);

  useLayoutEffect(() => {
    const el = inner.current;

    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const apply = (next: number) => {
      // jsdom reports 0 for everything; 'auto' keeps tests honest.
      setHeight(next > 0 ? next : 'auto');
    };
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (entry) {
        apply(entry.contentRect.height);
      }
    });

    ro.observe(el);
    apply(el.getBoundingClientRect().height);

    return () => ro.disconnect();
  }, []);

  return (
    <motion.div
      className={className}
      initial={false}
      animate={{ height }}
      transition={tween(DURATION.base, EASE.standard)}
      onAnimationStart={() => setAnimating(true)}
      onAnimationComplete={() => setAnimating(false)}
      style={{ overflow: animating ? 'hidden' : 'visible' }}
    >
      <div ref={inner}>{children}</div>
    </motion.div>
  );
};
